import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { adminBillingService } from '@/services/admin-billing.service';
import { ApiError } from '@/services/http';
import type { AdminPlanOverridePayload, MonitoringAccountRow } from '@/types/api';

const TIERS: AdminPlanOverridePayload['planTier'][] = ['free', 'plus', 'family'];

const STATUSES: AdminPlanOverridePayload['subscriptionStatus'][] = [
  'active',
  'trialing',
  'past_due',
  'canceled',
  'incomplete',
  'unpaid',
  'paused',
  null,
];

/** Statuses the server treats as a live paid entitlement. Mirrors
 *  `PAID_BILLING_STATUSES` in `server/src/common/subscription/entitlement.ts`. */
const PAID_STATUSES = new Set(['active', 'trialing', 'past_due']);

interface PlanOverrideDialogProps {
  /** The row being edited, or `null` when closed. */
  account: MonitoringAccountRow | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Admin plan override — comp an account onto a paid tier without Stripe, or
 * revoke it back to free.
 *
 * Refuses up-front when the row has a `stripeSubscriptionId`: those columns
 * are a mirror of Stripe and are re-derived on every `customer.subscription.*`
 * event, so an override there reverts at the next renewal. The server enforces
 * the same rule with a 409 — this is just the friendlier surface.
 */
export function PlanOverrideDialog({ account, onClose, onSaved }: PlanOverrideDialogProps) {
  const [tier, setTier] = useState<AdminPlanOverridePayload['planTier']>('family');
  const [status, setStatus] =
    useState<AdminPlanOverridePayload['subscriptionStatus']>('active');
  const [saving, setSaving] = useState(false);

  // Seed the form from the row each time a different account is opened.
  useEffect(() => {
    if (!account) return;
    setTier((account.planTier === 'plus' || account.planTier === 'family'
      ? account.planTier
      : 'free') as AdminPlanOverridePayload['planTier']);
    setStatus(
      (STATUSES.includes(account.subscriptionStatus as never)
        ? account.subscriptionStatus
        : null) as AdminPlanOverridePayload['subscriptionStatus'],
    );
  }, [account]);

  if (!account) return null;

  const stripeOwned = account.stripeSubscriptionId !== null;
  const tierIsPaid = tier !== 'free';
  const statusIsPaid = status !== null && PAID_STATUSES.has(status);
  // The server rejects this combination with a 400; block it here so the
  // admin sees why before spending a round-trip.
  const incoherent = tierIsPaid !== statusIsPaid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || stripeOwned || incoherent) return;
    setSaving(true);
    try {
      const next = await adminBillingService.overridePlan(account.id, {
        planTier: tier,
        subscriptionStatus: status,
      });
      toast.success(
        `${account.firstName} ${account.lastName} is now ${next.planTier}/${next.subscriptionStatus ?? 'null'}.`,
      );
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not change the plan.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-or-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-2xl border border-or-slate-200 bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-display text-[18px] font-bold text-or-slate-900">Change plan</h2>
        <p className="mt-1 text-[12px] text-or-slate-500">
          {account.firstName} {account.lastName} — currently {account.planTier}/
          {account.subscriptionStatus ?? 'null'}
        </p>

        {stripeOwned ? (
          <p className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-[12px] leading-relaxed text-rose-800">
            This account has a live Stripe subscription (
            <code className="font-mono text-[11px]">{account.stripeSubscriptionId}</code>). These
            columns mirror Stripe and are rewritten on every subscription event, so an override
            here would revert at the next renewal. Use <strong>Re-sync from Stripe</strong>
            instead, or comp them with a 100%-off coupon in Stripe.
          </p>
        ) : (
          <>
            <div className="mt-4 flex flex-col gap-3">
              <Field label="Tier">
                <select
                  value={tier}
                  onChange={(e) =>
                    setTier(e.target.value as AdminPlanOverridePayload['planTier'])
                  }
                  className="h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
                >
                  {TIERS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Subscription status">
                <select
                  value={status ?? ''}
                  onChange={(e) =>
                    setStatus(
                      (e.target.value === ''
                        ? null
                        : e.target.value) as AdminPlanOverridePayload['subscriptionStatus'],
                    )
                  }
                  className="h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
                >
                  {STATUSES.map((s) => (
                    <option key={s ?? 'null'} value={s ?? ''}>
                      {s ?? '(none)'}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            {incoherent ? (
              <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] leading-relaxed text-amber-900">
                A paid tier needs a paid status (active, trialing, past_due), and the free tier
                needs a non-paid one. As set, this would grant nothing.
              </p>
            ) : (
              <p className="mt-3 text-[12px] leading-relaxed text-or-slate-500">
                No Stripe subscription on this account, so nothing will overwrite this. Note that
                comped accounts still count as “paid” in the admin paid/free filter.
              </p>
            )}
          </>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" type="button" onClick={onClose} disabled={saving}>
            {stripeOwned ? 'Close' : 'Cancel'}
          </Button>
          {stripeOwned ? null : (
            <Button variant="primary" size="sm" type="submit" disabled={saving || incoherent}>
              {saving ? 'Saving…' : 'Save plan'}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
      {label}
      <span className="font-normal normal-case">{children}</span>
    </label>
  );
}
